import {
  Button,
  CardItem,
  Tag,
  Tooltip,
  Typography,
} from "@hellocarbo/lasagne";
import { ArrowRight, LucideIcon } from "lucide-react";
import { useContext } from "react";

import { PortalContainerContext } from "@/contexts";
import useSegmentEvent from "@/hooks/useSegmentEvent";

type OptionItemProps = {
  title: string;
  description: string;
  href: string;
  optionKey: string;
  tag?: string;
  Icon?: LucideIcon;
  shouldNotDisplayCTA?: boolean;
  tooltipContent: string;
  shouldOpenNewTab?: boolean;
};

function OptionItem({
  title,
  description,
  href,
  tag,
  Icon,
  optionKey,
  shouldNotDisplayCTA,
  tooltipContent,
  shouldOpenNewTab = false,
}: OptionItemProps) {
  const fullHref = `${href}?option=${encodeURIComponent(optionKey)}`;
  const { sendSegmentEvent } = useSegmentEvent();
  const { container } = useContext(PortalContainerContext);

  return (
    <CardItem className="gap-x-sm flex h-full items-center justify-between">
      <div className="gap-x-sm flex items-center">
        {Icon && (
          <Icon
            data-testid="lucide-icon"
            className="size-md text-grayscale-light shrink-0"
          />
        )}
        <div>
          <Typography weight="bold">{title}</Typography>
          <Typography
            step="-1"
            weight="semibold"
            className="text-grayscale mt-3xs"
          >
            {description}
          </Typography>
        </div>
      </div>
      <div className="gap-x-sm ml-auto flex items-center">
        {tag && <Tag intent="primary" text={tag} />}
        {!shouldNotDisplayCTA && (
          <Tooltip
            portalContainer={container as Element}
            side="top"
            content={tooltipContent}
            triggerAsChild
          >
            <Button
              shape="soft"
              intent="secondary"
              as="a"
              href={fullHref}
              size="small"
              Icon={ArrowRight}
              onClick={() => sendSegmentEvent(`option_${optionKey}`)}
              shouldOpenNewTab={shouldOpenNewTab}
            />
          </Tooltip>
        )}
      </div>
    </CardItem>
  );
}

export default OptionItem;
